import { produce } from 'immer';
import { TFormValues, TPerson, TSerialisedAddress } from './MSHForm.types';
import { v4 as uuidv4 } from 'uuid';
import { GetStateByStateKeyResponse } from '@iwoca/lapi-client/edge';

const removedUndefinedValues = (object: { [k: string]: any }) =>
    Object.fromEntries(
        Object.entries(object).filter(([, val]) => {
            return val !== undefined;
        })
    );

export const convertDeserialisedPersonToFormValues = (
    deserialisedPerson: TPerson
) => {
    const { uid, title, firstName, lastName, dateOfBirth, roles, addresses } =
        deserialisedPerson;
    const { day, month, year } = dateOfBirth!;
    const address = addresses![0];
    const sanitisedAddress = removedUndefinedValues(address);

    const mappedFormValues = {
        uid: uid,
        title: title,
        firstName: firstName,
        lastName: lastName,
        day: day,
        month: month,
        year: year,
        director: roles?.includes('director'),
        shareholder: roles?.includes('shareholder'),
        guarantor: roles?.includes('guarantor'),
        address: sanitisedAddress
    };

    return removedUndefinedValues(mappedFormValues);
};

export const createCustomerStateWithNewOrUpdatedPerson = (
    values: TFormValues,
    customerState: GetStateByStateKeyResponse
) => {
    const roles: ('director' | 'shareholder' | 'guarantor')[] = [];
    if (values.director) roles.push('director');
    if (values.shareholder) roles.push('shareholder');
    if (values.guarantor) roles.push('guarantor');

    const serialisedAddress: TSerialisedAddress = {
        uid: values.address.uid ? values.address.uid : uuidv4()
    };

    if (values.address.house_number)
        serialisedAddress.house_number = values.address.house_number;
    if (values.address.house_name)
        serialisedAddress.house_name = values.address.house_name;
    if (values.address.street_line_1)
        serialisedAddress.street_line_1 = values.address.street_line_1;
    if (values.address.street_line_2)
        serialisedAddress.street_line_2 = values.address.street_line_2;
    if (values.address.town) serialisedAddress.town = values.address.town;
    if (values.address.postcode)
        serialisedAddress.postcode = values.address.postcode;
    if (values.address.equifax_token)
        serialisedAddress.equifax_token = values.address.equifax_token;

    const newPersonSerialised = {
        uid: values.uid ? values.uid : uuidv4(),
        title: values.title,
        first_name: values.firstName,
        last_name: values.lastName,
        date_of_birth: `${values.year}-${values.month}-${values.day}`,
        roles: roles,
        residential_addresses: [serialisedAddress]
    };

    const newState = produce(customerState, (draft) => {
        const people = draft.data.application!.people!.filter(
            (person) => person.uid !== values.uid
        );

        people.push(newPersonSerialised);

        draft.data.application!.people = people;
    });

    return newState;
};
